import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useIsMobile } from "@/hooks/use-mobile";
import { InfoPopover } from "./InfoPopover";
import { RiskProfileDetails } from "./RiskProfileDetails";
import { useToast } from "@/hooks/use-toast";

interface RiskScoreCardProps {
  riskScore: number;
  riskProfile: string;
}

const RiskScoreCard = ({ riskScore, riskProfile }: RiskScoreCardProps) => {
  const isMobile = useIsMobile();
  const { toast } = useToast();

  return (
    <div className="bg-white rounded-lg p-6 shadow-sm border">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4">
        <div className="flex items-start sm:items-center gap-2 mb-2 sm:mb-0">
          <h2 className="text-lg font-semibold text-navy">AltsIQ Portfolio Risk Score</h2>
          <InfoPopover />
        </div>
        <Dialog>
          <DialogTrigger asChild>
            <Button variant="ghost" size="sm" className="shrink-0">
              Learn More
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto mt-16 sm:mt-0">
            <div className="flex items-center justify-between">
              <DialogTitle className="text-lg font-semibold text-navy">
                Understanding AltsIQ Risk Score
              </DialogTitle>
            </div>
            <DialogDescription>
              <div className="space-y-4 pt-4 text-left">
                <p className="text-gray-800">
                  The AltsIQ Risk Score is a proprietary measure that evaluates your portfolio's risk level 
                  on a scale from 1 to 5, based on your asset allocation strategy. This score helps you 
                  understand and manage your investment risk exposure.
                </p>
                <RiskProfileDetails />
              </div>
            </DialogDescription>
          </DialogContent>
        </Dialog>
      </div>
      <div className="flex items-baseline gap-3 mb-4">
        <span className="text-3xl font-bold text-navy">{riskScore}</span>
        <span className="text-base text-gray-600">{riskProfile}</span>
      </div>
      
      {/* Risk indicator bar */}
      <div className="relative w-full h-8 flex items-center">
        {/* Background gradient bar */}
        <div className="absolute inset-0 flex items-center px-2">
          <div 
            className="w-full h-2 rounded-full"
            style={{
              background: `linear-gradient(90deg, 
                #1A2B4C 0%,    /* Space cadet */
                #243C66 12.5%, /* Yale Blue */
                #2D4C7F 25%,   /* YinMn Blue */
                #536276 37.5%, /* Payne's gray */
                #79786C 50%,   /* Battleship gray */
                #9F8E63 62.5%, /* Lion */
                #C5A359 75%,   /* Satin sheen gold */
                #BD9A4E 87.5%, /* Goldenrod */
                #B49042 100%   /* Dark goldenrod */
              )`
            }}
          />
        </div>
        
        {/* Indicator dot */}
        <div 
          className="absolute h-4 w-4 bg-black rounded-full border-2 border-white shadow-md"
          style={{
            left: `calc(${Math.max(0, Math.min(((riskScore - 1) / 4) * 100, 98))}%)`,
            transform: 'translateX(-50%)',
          }}
        />
      </div>
    </div>
  );
};

export default RiskScoreCard;